import Modal from "components/DesignSystem/Modal/Modal";
import { SalesTaxEntityDetailsBase } from "components/SalesTaxOnboardingSubmit/SalesTaxEntityDetailsBase";
import { Form, Formik, FormikProps } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import isEqual from "lodash/isEqual";
import {
  useEditEntityDataAttributesMutation,
  useGetEntityDataAttributesQuery,
} from "store/apis/entity";
import { EntityDataAttribute } from "types/Models/entity";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/design/loader";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import { personaAutoFillSalesTax } from "formValidations/autofillSchema";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import { useRef, useState } from "react";
import { useGetAutofillQuery } from "store/apis/autofill";
import { Button } from "components/DesignSystem/Button/Button";
import {
  useGetSalexTaxBusinessQuery,
  useUpdateSalesTaxBusinessMutation,
} from "store/apis/salesTax";

export const BusinessDetailsModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast } = useToast();
  const query = useQuery();
  let entityId = query.get("entity") as string;
  const [hasMissingDetails, setHasMissingDetails] = useState(false);
  const formikRef = useRef<FormikProps<{ owner_id: string }>>(null);

  const {
    data: currentEntityDetails = [],
    isLoading,

    isSuccess,
  } = useGetEntityDataAttributesQuery(
    {
      groupId: groupId!,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const { data: salesTaxBusiness, isLoading: isSalesTaxbusinessLoading } =
    useGetSalexTaxBusinessQuery(
      {
        entityId: entityId!,
      },
      { skip: !entityId }
    );
  const [editEntityDataAttributes, { isLoading: isUpdatingEntityData }] =
    useEditEntityDataAttributesMutation();

  const isEmpty = currentEntityDetails?.length === 0;
  const [updateSalesTaxBusiness, { isLoading: isUpdatingPersona }] =
    useUpdateSalesTaxBusinessMutation();

  const initialValues = currentEntityDetails.reduce((acc, field) => {
    acc[field.name] = field.value;
    return acc;
  }, {} as Record<string, EntityDataAttribute["value"]>);

  const onChange = debounce(
    async ({
      name,
      value,
    }: {
      name: string;
      value: EntityDataAttribute["value"];
    }) => {
      const payload = { [name]: value };
      const initialValue = initialValues[name];

      const isSameValue = isEqual(
        JSON.stringify(value),
        JSON.stringify(initialValue)
      );

      if (isSameValue) {
        return;
      }

      try {
        await editEntityDataAttributes({
          groupId,
          entityId,
          payload,
        }).unwrap();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  );

  const { data: autofills = [] } = useGetAutofillQuery(
    {
      groupId,
      autofillKey: "authorized_persons",
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const onPersonaChange = async (uuid: string) => {
    const currentAutofill = autofills.find((a) => a.uuid === uuid);
    try {
      await personaAutoFillSalesTax.validate(currentAutofill);
      setHasMissingDetails(() => false);
    } catch (error) {
      setHasMissingDetails(() => true);
      return;
    }

    try {
      await updateSalesTaxBusiness({
        entityId: entityId!,
        payload: {
          owner_id: uuid,
        },
      }).unwrap();
    } catch (error: any) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error
      );
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content
        useCustomOverlay
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <Modal.Header>
          <Modal.Title>
            <div className="t-flex t-items-center t-gap-2">
              Business details{" "}
              {
                <Async.Root
                  isLoading={
                    isUpdatingEntityData ||
                    isUpdatingPersona ||
                    isSalesTaxbusinessLoading
                  }
                  customLoader={<Loader customType="secondary" />}
                  isEmpty={isEmpty}
                  isSuccess={isSuccess}
                >
                  <Async.Empty>
                    <></>
                  </Async.Empty>
                  <Async.Success>
                    <span className="t-flex t-text-text-30 t-text-body-sm t-items-center t-gap-1.5 t-transition-all">
                      <img src={DoubleGreenTick} alt="Saved" />
                      Changes saved
                    </span>
                  </Async.Success>
                </Async.Root>
              }
            </div>
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isEmpty={isEmpty}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Success>
              <Formik
                initialValues={{ owner_id: salesTaxBusiness?.owner_id || "" }}
                enableReinitialize
                innerRef={formikRef}
                onSubmit={() => {}}
              >
                {({ values, setFieldValue }) => (
                  <div className=" t-overflow-auto">
                    <Form>
                      <SalesTaxEntityDetailsBase onChange={onChange} />
                      <div className="t-flex t-flex-col t-gap-4 t-mt-2">
                        <SelectAutofillCombobox
                          type="authorized_persons"
                          name="owner_id"
                          onChange={(uuid) => {
                            setFieldValue("owner_id", uuid);
                            onPersonaChange(uuid);
                          }}
                          label="Business owner"
                          placeholder="Select business owner"
                          entityId={entityId}
                          required
                          selected={values.owner_id}
                          customValidation={{
                            authorized_persons: personaAutoFillSalesTax,
                          }}
                        />
                        {hasMissingDetails && (
                          <p className="t-flex t-items-center t-text-red-50 t-text-body-sm -t-mt-3">
                            Some required information is missing. Please provide
                            the mandatory details to proceed.
                          </p>
                        )}
                      </div>
                    </Form>
                  </div>
                )}
              </Formik>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button customType="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
